import { useUnistyles } from "react-native-unistyles";
import { useCallback, useId } from "react";

export type StripeState = "armed" | "disarmed";

/**
 * Hook that returns a function to get the stripe color for a given alarm state.
 * - Armed: dark purple (alarmStates.armed) - these are the thick stripes
 * - Disarmed: medium grey (grey[400]) for stripes on light grey background
 */
export function useStripeColor() {
  const { theme } = useUnistyles();
  return useCallback(
    (state: StripeState): string => {
      return state === "armed" ? (theme.colors.alarmStates.armed as string) : (theme.colors.grey[400] as string);
    },
    [theme],
  );
}

/**
 * Hook that returns a function to get the background color for a given alarm state.
 * - Armed: light purple (alarmStates.armed) - background for dark purple stripes
 * - Disarmed: light grey (alarmStates.disarmed) for two-tone grey effect with stripes
 */
export function useAlarmStateBackgroundColor() {
  const { theme } = useUnistyles();
  return useCallback(
    (state: StripeState): string => {
      return state === "armed" ? (theme.colors.purple[100] as string) : (theme.colors.alarmStates.disarmed as string);
    },
    [theme],
  );
}

/**
 * Hook that returns an SVG pattern id unique to the calling component.
 *
 * The schedule viewer renders many stripe patterns on one web page (every day
 * column of every mounted week, plus legend and list swatches). A shared id
 * like `stripe-armed` is then duplicated across the document, and which copy
 * `url(#…)` resolves to differs between browsers — Edge and Safari showed
 * exceptions with no fill, so they read as disarmed. Scoping the id to the
 * component makes each `url(#…)` point at the pattern defined beside it.
 *
 * `useId` values contain characters (`:` or `«»` depending on the React
 * version) that are unsafe in an SVG fragment identifier, so they are stripped.
 */
export function useStripePatternId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}
